import { LitElement, html } from "lit";
import { customElement, property, queryAssignedElements } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";

import DeviceController from "@/controllers/device-controller";

import type AtlasNavbar from "@/components/structure/atlas-navbar/atlas-navbar";
import type AtlasNewSidebar from "@/components/structure/atlas-new-sidebar/atlas-new-sidebar";
import type AtlasSidebar from "@/components/structure/atlas-sidebar/atlas-sidebar";

import style from "./atlas-screen.scss";

export type ScreenProps = {
    fullscreen: boolean;
    white: boolean;
};

/**
 * @slot navbar - Slot usado para incluir uma navbar
 * @slot sidebar - Slot usado para incluir uma sidebar
 * @slot - Slot padrão usado para incluir conteúdos no corpo da tela
 */
@customElement("atlas-screen")
export default class AtlasScreen extends LitElement {
    static styles = style;

    /** Indica que toda a tela será ocupada */
    @property({ type: Boolean, reflect: true }) fullscreen: boolean = false;

    /** Indica que a cor de fundo será branca */
    @property({ type: Boolean, reflect: true }) white: boolean = false;

    @queryAssignedElements({ selector: "atlas-navbar", flatten: true, slot: "navbar" })
    private _slottedNavbar: Array<AtlasNavbar>;

    @queryAssignedElements({ selector: "atlas-sidebar, atlas-new-sidebar", flatten: true, slot: "sidebar" })
    private _slottedSidebar: Array<AtlasSidebar> | Array<AtlasNewSidebar>;

    private _deviceController = new DeviceController(this, this.onChangeScreenType.bind(this));

    public constructor() {
        super();

        this.onSidebarToggleClick = this.onSidebarToggleClick.bind(this);
        this.onMobileSidebarToggle = this.onMobileSidebarToggle.bind(this);
    }

    /** @internal */
    public connectedCallback() {
        super.connectedCallback?.();

        this.addEventListener("atlas-sidebar-toggle-click", this.onSidebarToggleClick);
        this.addEventListener("atlas-sidebar-toggle", this.onMobileSidebarToggle);
    }

    /** @internal */
    public disconnectedCallback(): void {
        super.disconnectedCallback?.();

        this.removeEventListener("atlas-sidebar-toggle-click", this.onSidebarToggleClick);
        this.removeEventListener("atlas-sidebar-toggle", this.onMobileSidebarToggle);
    }

    /**
     * Retorna a referência do componente atlas-sidebar presente no slot correspondente (se houver)
     * @returns {AtlasSidebar | AtlasNewSidebar} - A referência do componente atlas-sidebar ou atlas-new-sidebar
     */
    public getSlottedSidebar(): AtlasSidebar | AtlasNewSidebar {
        return this._slottedSidebar?.[0];
    }

    /**
     * Retorna a referência do componente atlas-navbar presente no slot correspondente (se houver)
     * @returns {AtlasNavbar} - A referência do componente atlas-navbar
     */
    public getSlottedNavbar(): AtlasNavbar {
        return this._slottedNavbar?.[0];
    }

    private toggleCollapseSidebar() {
        this.getSlottedSidebar().toggleCollapse();
    }

    private onChangeScreenType() {
        this.onMobileSidebarToggle();
    }

    private onSidebarToggleClick() {
        this.toggleCollapseSidebar();
        window.scroll(0, 0);
    }

    private onMobileSidebarToggle() {
        const sidebar = this.getSlottedSidebar();

        if (sidebar) {
            setTimeout(() => {
                document.body.classList.toggle("disable-scroll", this._deviceController.isMobile && !sidebar.collapsed);
            }, 0);
        }
    }

    private renderSidebar() {
        return html`
            <div class="screen-sidebar">
                <slot name="sidebar"></slot>
            </div>
        `;
    }

    private renderNavbar() {
        return html`
            <div class="screen-navbar">
                <slot name="navbar"></slot>
            </div>
        `;
    }

    private renderBody() {
        const bodyClass = {
            "screen-body": true,
            "background-white": this.white
        };

        return html`
            <div class=${classMap(bodyClass)} part="screen-body">
                <slot></slot>
            </div>
        `;
    }

    /** @internal */
    public render() {
        return html`
            <div class="screen-wrapper">
                ${this.renderSidebar()}
                <div class="screen-content">${this.renderNavbar()} ${this.renderBody()}</div>
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "atlas-screen": AtlasScreen;
    }
}
